import { FunctionComponent, useMemo } from 'react'
import styles from './mealHandler.module.scss'
import { useTranslation } from 'react-i18next'
import { MealDayDetailByType, MealDetail, MealType, MealTypeDetail } from '@/types/Meal'
import MealTypeCard from './mealTypeCard.tsx'

interface MealDetailedViewProps {
    mealDayDetailByType: MealDayDetailByType | null
    canUpdate: boolean
    mealTypeTranslations: Record<string, string>
    getOrderAdjustment: (detail: MealDetail, contextKey?: string) => number
    handleAdjustOrder: (detail: MealDetail, delta: number, contextKey?: string) => void
    handleFreezeMealType: (type: string, sectionIds: number[]) => void
}

const MealDetailedView: FunctionComponent<MealDetailedViewProps> = ({
    mealDayDetailByType,
    canUpdate,
    mealTypeTranslations,
    getOrderAdjustment,
    handleAdjustOrder,
    handleFreezeMealType
}: MealDetailedViewProps) => {
    const { t } = useTranslation()

    const typeGroups: { type: MealType; typeDetails: MealTypeDetail[] }[] = useMemo(() => {
        if (!mealDayDetailByType) return []

        const map: Map<string, MealTypeDetail[]> = new Map()
        for (const td of mealDayDetailByType.typeDetails) {
            if (td.details.length === 0) continue
            if (!map.has(td.type)) map.set(td.type, [])
            map.get(td.type)!.push(td)
        }

        return [...map.entries()].map(([type, typeDetails]) => ({ type: type as MealType, typeDetails }))
    }, [mealDayDetailByType])

    if (typeGroups.length === 0) {
        return (
            <div className={styles.MealList}>
                <div className={styles.Text}>
                    <p>{t('noMealsThisDay')}</p>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.MealList}>
            {typeGroups.map(({ type, typeDetails }) => (
                <MealTypeCard
                    key={type}
                    type={type}
                    typeDetails={typeDetails}
                    canUpdate={canUpdate}
                    mealTypeTranslation={mealTypeTranslations[type] || type}
                    getOrderAdjustment={getOrderAdjustment}
                    handleAdjustOrder={handleAdjustOrder}
                    handleFreezeMealType={handleFreezeMealType}
                />
            ))}
        </div>
    )
}

export default MealDetailedView
